import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StudentNavigation from '../../components/ui/StudentNavigation';
import NotificationCenter from '../../components/ui/NotificationCenter';
import SessionStatusIndicator from '../../components/ui/SessionStatusIndicator';
import InstituteInfo from './components/InstituteInfo';
import TestimonialSection from './components/TestimonialSection';
import LivePresentationCard from './components/LivePresentationCard';
import InteractionPanel from './components/InteractionPanel';
import { instituteService } from '../../services/instituteService';
import { boothEnhancementsService } from '../../services/boothEnhancementsService';
import { testimonialService } from '../../services/testimonialService';
import { notificationService } from '../../services/notificationService';
import { leadService } from '../../services/leadService';
import { counsellorPresenceService } from '../../services/counsellorPresenceService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { errorHandler } from '../../utils/errorHandler';
import Button from '../../components/ui/Button';
import ResourceSidebar from './components/ResourceSidebar';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

import { useCursorNavigation } from '../../hooks/useCursorNavigation';
import { useSwipeNavigation } from '../../hooks/useSwipeNavigation';
import { useParallaxEffect } from '../../hooks/useParallaxEffect';





const VirtualBooth = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [instituteData, setInstituteData] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [resources, setResources] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [visitStartTime] = useState(Date.now());
  const [notifications, setNotifications] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);
  const [allInstituteIds, setAllInstituteIds] = useState([]);
  const [applicationLink, setApplicationLink] = useState(null);
  const [counsellorOnline, setCounsellorOnline] = useState(false);
  const [instituteUserId, setInstituteUserId] = useState(null);

  // Fetch all institute IDs immediately on mount (not dependent on user)
  useEffect(() => {
    fetchAllInstituteIds();
  }, []);

  // Cursor-based navigation handler
  const handleCursorNavigate = (direction) => {
    if (!allInstituteIds?.length || allInstituteIds?.length <= 1) return;
    
    const currentIndex = allInstituteIds?.indexOf(id);
    if (currentIndex === -1) return;
    
    let nextIndex;
    if (direction === 'prev') {
      nextIndex = currentIndex === 0 ? allInstituteIds?.length - 1 : currentIndex - 1;
    } else {
      nextIndex = currentIndex === allInstituteIds?.length - 1 ? 0 : currentIndex + 1;
    }
    
    const nextInstituteId = allInstituteIds?.[nextIndex];
    if (nextInstituteId) {
      navigate(`/virtual-booth/${nextInstituteId}`);
    }
  };

  // Enable cursor navigation and swipe gestures - recalculate when allInstituteIds changes
  const navigationEnabled = allInstituteIds?.length > 1;
  
  // Add debug logging to verify navigation state
  useEffect(() => {
    console.log('Navigation state updated:', {
      allInstituteIdsCount: allInstituteIds?.length,
      navigationEnabled,
      currentId: id
    });
  }, [allInstituteIds, navigationEnabled, id]);
  
  const { isInLeftZone, isInRightZone } = useCursorNavigation(
    handleCursorNavigate,
    navigationEnabled,
    800 // 800ms hover delay
  );
  
  const { isSwiping, swipeDirection, swipeProgress } = useSwipeNavigation(
    handleCursorNavigate,
    navigationEnabled,
    50 // 50px swipe threshold
  );

  // Parallax effect for 3D depth
  const { parallaxStyle, backgroundParallax, foregroundParallax } = useParallaxEffect(
    true, // Always enabled
    0.05 // Intensity
  );

  useEffect(() => {
    // Validate institute ID format
    const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(id);
    
    // Silently handle invalid IDs (like ':id' from external navigation requests)
    // Only show error toast for malformed UUIDs that look like real attempts
    if (!id || id === ':id' || id === 'undefined' || id === 'null') {
      // Silent redirect for placeholder/invalid values - don't show error to user
      console.warn('Placeholder booth ID detected, ignoring navigation request:', id);
      navigate('/exhibition-hall', { replace: true });
      return;
    }
    
    if (!isValidUUID) {
      // Only log and show toast for malformed UUIDs that look like real attempts
      console.error('Invalid institute ID format:', id);
      toast?.error('Invalid booth ID format. Redirecting to exhibition hall...');
      navigate('/exhibition-hall', { replace: true });
      return;
    }

    // Log the booth ID being loaded
    console.log('Loading booth for institute ID:', id);
    
    // Only fetch data if we have a valid ID
    fetchAllData();
    trackVisit();

    return () => {
      trackVisitDuration();
    };
  }, [id]);

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  // Subscribe to real-time counsellor presence updates
  useEffect(() => {
    if (!instituteUserId) return;
    const unsubscribe = counsellorPresenceService?.subscribeToPresence(
      instituteUserId,
      (isOnline) => setCounsellorOnline(isOnline)
    );
    // Re-check every 60 seconds in case real-time misses an update
    const interval = setInterval(async () => {
      const isOnline = await counsellorPresenceService?.isCounsellorOnline(instituteUserId);
      setCounsellorOnline(isOnline);
    }, 60000);
    return () => {
      unsubscribe?.();
      clearInterval(interval);
    };
  }, [instituteUserId]);

  const fetchAllData = async () => {
    // Safety check: Don't proceed if ID is invalid
    if (!id || id === ':id' || id === 'undefined' || id === 'null') {
      console.warn('[fetchAllData] Called with invalid ID:', id);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      const [instituteInfo, gallery, reviewsData, ratingData, resourcesData, testimonialsData] = await errorHandler?.withRetry(
        async () => Promise.all([
          instituteService?.getInstituteForBooth(id),
          boothEnhancementsService?.getBoothGallery(id),
          boothEnhancementsService?.getBoothReviews(id),
          boothEnhancementsService?.getAverageRating(id),
          boothEnhancementsService?.getBoothResources(id),
          testimonialService?.getApprovedTestimonials(id)
        ]),
        { 
          maxRetries: 2, 
          retryDelay: 1000,
          onRetry: (attempt) => {
            toast?.info(`Loading booth data... (Attempt ${attempt}/2)`);
          }
        }
      );

      // Enhanced validation and logging
      if (!instituteInfo) {
        console.error('No institute data returned for booth ID:', id);
        toast?.error('Institute not found. This booth may not exist or has been removed.');
        setTimeout(() => navigate('/exhibition-hall', { replace: true }), 2000);
        return;
      }
      
      console.log('Institute data loaded successfully:', {
        instituteId: id,
        instituteName: instituteInfo?.institute_name,
        hasVirtualBooths: !!instituteInfo?.virtual_booths,
        virtualBoothsCount: instituteInfo?.virtual_booths?.length || 0,
        hasApplicationLinks: !!(instituteInfo?.virtual_booths?.[0]?.application_links)
      });

      const transformedData = {
        id: id, // Add institute ID for navigation purposes
        name: instituteInfo?.institute_name || 'Institute Name',
        location: `${instituteInfo?.user?.city || 'City'}, ${instituteInfo?.user?.state || 'State'}`,
        logo: instituteInfo?.logo_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e7bb1524-1766485632262.png',
        logoAlt: `${instituteInfo?.institute_name || 'Institute'} logo`,
        banner: instituteInfo?.banner_url || 'https://images.unsplash.com/photo-1621862051799-f85dea701c16',
        bannerAlt: `${instituteInfo?.institute_name || 'Institute'} campus view`,
        about: instituteInfo?.description || 'No description available.',
        accreditations: [],
        facilities: [],
        chatEnabled: instituteInfo?.chat_enabled ?? false,
        videoEnabled: instituteInfo?.video_enabled ?? false,
        tour_360_enabled: instituteInfo?.tour_360_enabled ?? false,
        whatsappEnabled: instituteInfo?.whatsapp_enabled ?? false,
        courses: instituteInfo?.courses?.map(course => ({
          name: course?.course_name || 'Course Name',
          duration: course?.duration || 'N/A',
          description: `${course?.course_type || ''} program in ${course?.category || 'various fields'}`,
          specializations: course?.specialization ? [course?.specialization] : []
        })) || [],
        eligibility: [
          {
            program: 'All Programs',
            requirements: [
              'Please contact the institute for specific eligibility requirements',
              `Email: ${instituteInfo?.user?.email || 'N/A'}`,
              `Phone: ${instituteInfo?.user?.phone || 'N/A'}`
            ]
          }
        ],
        fees: instituteInfo?.courses?.map(course => {
          // Check if institute wants to show fee
          const showFee = course?.show_fee !== false;
          const feeAmount = showFee && course?.fees ? course?.fees : 0;
          
          return {
            program: course?.course_name || 'Program',
            amount: feeAmount,
            perYear: feeAmount,
            scholarships: showFee ? 'Contact institute' : 'Fee details available on request'
          };
        }) || [],
        contact: {
          address: `${instituteInfo?.user?.city || ''}, ${instituteInfo?.user?.state || ''}`?.trim() || 'Address not available',
          phone: instituteInfo?.whatsapp_number || instituteInfo?.user?.phone || 'N/A',
          email: instituteInfo?.user?.email || 'N/A',
          coordinates: { lat: 0, lng: 0 },
          hours: [
            { days: 'Monday - Friday', time: '9:00 AM - 5:00 PM' },
            { days: 'Saturday', time: '10:00 AM - 2:00 PM' },
            { days: 'Sunday', time: 'Closed' }
          ]
        }
      };

      setInstituteData(transformedData);
      setGalleryImages(gallery || []);
      setReviews(reviewsData || []);
      setAverageRating(ratingData?.average || 0);
      setTotalReviews(ratingData?.count || 0);
      setResources(resourcesData || []);
      setTestimonials(testimonialsData || []);
      
      // Extract application link from virtual_booths data
      const boothData = instituteInfo?.virtual_booths?.[0];
      console.log('🔍 [APPLICATION LINK DEBUG] Full booth data:', boothData);
      console.log('🔍 [APPLICATION LINK DEBUG] application_links field:', boothData?.application_links);
      console.log('🔍 [APPLICATION LINK DEBUG] Is array?', Array.isArray(boothData?.application_links));
      console.log('🔍 [APPLICATION LINK DEBUG] Array length:', boothData?.application_links?.length);
      
      if (boothData?.application_links && Array.isArray(boothData?.application_links) && boothData?.application_links?.length > 0) {
        console.log('✅ [APPLICATION LINK DEBUG] Setting from array:', boothData?.application_links?.[0]);
        setApplicationLink(boothData?.application_links?.[0]);
      } else if (typeof boothData?.application_links === 'string') {
        console.log('✅ [APPLICATION LINK DEBUG] Setting from string:', boothData?.application_links);
        setApplicationLink(boothData?.application_links);
      } else {
        console.log('❌ [APPLICATION LINK DEBUG] No valid application link found, setting to null');
        setApplicationLink(null);
      }

      // Store institute user_id for presence tracking
      const instUserId = instituteInfo?.user_id || instituteInfo?.user?.id || null;
      setInstituteUserId(instUserId);

      // Check initial counsellor online status
      if (instUserId) {
        const isOnline = await counsellorPresenceService?.isCounsellorOnline(instUserId);
        setCounsellorOnline(isOnline);
      }

      setRetryCount(0);
    } catch (err) {
      console.error('Error fetching booth data:', {
        error: err,
        instituteId: id,
        errorMessage: err?.message,
        errorStack: err?.stack
      });
      const appError = errorHandler?.parseSupabaseError(err);
      setError(appError?.message);
      toast?.error(appError?.message || 'Failed to load booth data');
      errorHandler?.logError(appError, { context: 'Virtual Booth Load', instituteId: id });
      
      // If institute not found, redirect after showing error
      if (err?.message?.includes('not found') || err?.message?.includes('PGRST116')) {
        setTimeout(() => navigate('/exhibition-hall', { replace: true }), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const notifs = await notificationService?.getNotifications(user?.id, { limit: 10 });
      setNotifications(notifs || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Don't show error toast for notifications - non-critical
    }
  };

  const fetchAllInstituteIds = async () => {
    try {
      const institutes = await instituteService?.getInstitutes();
      const ids = institutes?.map(inst => inst?.id)?.filter(Boolean) || [];
      setAllInstituteIds(ids);
      console.log('Loaded institute IDs for navigation:', ids?.length);
    } catch (error) {
      console.error('Error fetching institute IDs:', error);
      // Non-critical - navigation will just be disabled
    }
  };

  const trackVisit = async () => {
    if (!id || id === ':id' || id === 'undefined' || id === 'null') {
      console.warn('Skipping visit tracking - invalid institute ID');
      return;
    }
    
    try {
      const result = await boothEnhancementsService?.trackBoothVisit({
        instituteId: id,
        studentId: user?.id || null,
        deviceType: /Mobile|Android|iPhone/i?.test(navigator.userAgent) ? 'mobile' : 'desktop',
        referrerUrl: document.referrer || null
      });
      
      if (result === null) {
        console.log('Visit tracking skipped (non-critical)');
      }

      // Auto-capture lead on booth visit (only for logged-in students)
      if (user?.id) {
        leadService?.captureLeadSilently(
          user?.id,
          id,
          'booth_visit',
          null,
          true
        );
      }
    } catch (error) {
      console.error('Error tracking visit (non-critical):', error?.message || error);
    }
  };

  const trackVisitDuration = async () => {
    // Only track duration for logged-in users
    if (!user) return;
    
    const durationSeconds = Math.floor((Date.now() - visitStartTime) / 1000);
    
    // Only track if user spent meaningful time (more than 5 seconds)
    if (durationSeconds < 5) return;
    
    try {
      await boothEnhancementsService?.trackBoothVisit({
        instituteId: id,
        studentId: user?.id,
        visitDurationSeconds: durationSeconds
      });
    } catch (error) {
      console.error('Error tracking duration:', error);
      // Don't show error toast - non-critical
    }
  };

  const handleSubmitReview = async (reviewData) => {
    if (!user) {
      toast?.warning('Please login to submit a review');
      navigate('/student-registration');
      return;
    }

    try {
      await boothEnhancementsService?.submitReview({
        instituteId: id,
        studentId: user?.id,
        ...reviewData
      });

      toast?.success('Review submitted successfully!');

      const [reviewsData, ratingData] = await Promise.all([
        boothEnhancementsService?.getBoothReviews(id),
        boothEnhancementsService?.getAverageRating(id)
      ]);
      
      setReviews(reviewsData || []);
      setAverageRating(ratingData?.average || 0);
      setTotalReviews(ratingData?.count || 0);
    } catch (error) {
      const appError = errorHandler?.parseSupabaseError(error);
      toast?.error(appError?.message);
      errorHandler?.logError(appError, { context: 'Submit Review', instituteId: id });
    }
  };

  const handleBookingConfirm = async (bookingData) => {
    if (!user) {
      toast?.warning('Please login to book a session');
      navigate('/student-registration');
      return;
    }

    try {
      await boothEnhancementsService?.createBooking({
        instituteId: id,
        studentId: user?.id,
        ...bookingData
      });

      // Capture lead on video session booking
      leadService?.captureLeadSilently(user?.id, id, 'video_session', null, true);

      setShowBookingModal(false);
      toast?.success('Session confirmed! Check your dashboard for details.');
    } catch (error) {
      const appError = errorHandler?.parseSupabaseError(error);
      // Handle double-booking gracefully
      if (error?.message?.includes('unique') || error?.code === '23505') {
        toast?.error('This slot was just booked by someone else. Please select another time.');
      } else {
        toast?.error(appError?.message || 'Failed to book session. Please try again.');
      }
      errorHandler?.logError(appError, { context: 'Create Booking', instituteId: id });
    }
  };

  const handleResourceDownload = async (resourceId) => {
    try {
      await boothEnhancementsService?.trackResourceDownload(resourceId);
    } catch (error) {
      console.error('Error tracking download:', error);
      // Don't show error toast - non-critical
    }
  };

  const handleMarkAsRead = (notificationId) => {
    setNotifications(prev =>
      prev?.map(notif =>
        notif?.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
  };

  const handleClearAll = () => {
    setNotifications([]);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    fetchAllData();
  };

  const handleNavigateToBooth = (direction) => {
    handleCursorNavigate(direction);
  };

  const presentations = [];

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading virtual booth...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <div className="flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={64} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Failed to Load Booth</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={handleRetry} iconName="RefreshCw" iconPosition="left">
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate('/exhibition-hall')}>
                Back to Exhibition Hall
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!instituteData) {
    return (
      <div className="min-h-screen bg-background">
        <StudentNavigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <p className="text-gray-600 text-lg">Institute not found</p>
            <Button onClick={() => navigate('/exhibition-hall')} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
              Back to Exhibition Hall
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <StudentNavigation />
      {/* Cursor Navigation Zones - Visual Feedback */}
      {navigationEnabled && (
        <>
          {/* Left Navigation Zone */}
          <div
            className={`fixed left-0 top-0 bottom-0 w-[10%] z-40 pointer-events-none transition-all duration-300 ${
              isInLeftZone ? 'bg-gradient-to-r from-blue-500/20 to-transparent' : ''
            }`}
          >
            {isInLeftZone && (
              <div className="absolute left-4 top-1/2 -translate-y-1/2 animate-pulse">
                <Icon name="ChevronLeft" size={48} className="text-blue-500 drop-shadow-lg" />
              </div>
            )}
          </div>

          {/* Right Navigation Zone */}
          <div
            className={`fixed right-0 top-0 bottom-0 w-[10%] z-40 pointer-events-none transition-all duration-300 ${
              isInRightZone ? 'bg-gradient-to-l from-blue-500/20 to-transparent' : ''
            }`}
          >
            {isInRightZone && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2 animate-pulse">
                <Icon name="ChevronRight" size={48} className="text-blue-500 drop-shadow-lg" />
              </div>
            )}
          </div>

          {/* Always Visible Navigation Buttons */}
          <button
            onClick={() => handleCursorNavigate('prev')}
            className="fixed left-4 top-[60%] -translate-y-1/2 z-50 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110 group"
            aria-label="Previous booth"
          >
            <Icon name="ChevronLeft" size={32} className="text-gray-700 group-hover:text-blue-600" />
          </button>

          <button
            onClick={() => handleCursorNavigate('next')}
            className="fixed right-4 top-[60%] -translate-y-1/2 z-50 bg-white/90 hover:bg-white shadow-lg rounded-full p-3 transition-all duration-200 hover:scale-110 group"
            aria-label="Next booth"
          >
            <Icon name="ChevronRight" size={32} className="text-gray-700 group-hover:text-blue-600" />
          </button>

          {/* Navigation Hint Text */}
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-blue-600/90 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg animate-bounce">
            ← Swipe or hover on edges to navigate →
          </div>

          {/* Swipe Progress Indicator (Mobile) */}
          {isSwiping && swipeProgress > 0 && (
            <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none">
              <div className="bg-white/90 rounded-full p-4 shadow-2xl">
                <Icon
                  name={swipeDirection === 'prev' ? 'ChevronLeft' : 'ChevronRight'}
                  size={64}
                  className="text-blue-600"
                  style={{ opacity: swipeProgress }}
                />
              </div>
            </div>
          )}
        </>
      )}
      <div className="pt-[60px]">
        {/* Full-Width Booth Banner Image with Parallax - Stretched to Screen Edges */}
        <div className="relative h-72 md:h-96 lg:h-[28rem] w-full overflow-hidden">
          <div style={backgroundParallax}>
            <Image
              src={instituteData?.banner}
              alt={instituteData?.bannerAlt}
              className="w-full h-full object-cover scale-110"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-6 left-4 md:bottom-8 md:left-6 lg:bottom-10 lg:left-8 flex items-end gap-4 md:gap-5" style={foregroundParallax}>
            <div className="w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 bg-card rounded-lg overflow-hidden border-2 md:border-4 border-card flex-shrink-0">
              <Image
                src={instituteData?.logo}
                alt={instituteData?.logoAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-white">
                {instituteData?.name}
              </h1>
              <p className="text-sm md:text-base text-white/90">{instituteData?.location}</p>
            </div>
          </div>
        </div>

        {/* Content Below Banner with Parallax */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10" style={parallaxStyle('midground')}>
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <Button
              variant="ghost"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => navigate('/student-dashboard')}>

              Back to Dashboard
            </Button>

            <div className="flex items-center gap-3 md:gap-4">
              <SessionStatusIndicator
                isOnline={true}
                activeChats={2}
                activeVideoCalls={0}
                onChatClick={() => navigate('/chat-interface')}
                onVideoClick={() => navigate('/video-counseling')} />

              <NotificationCenter
                notifications={notifications}
                onMarkAsRead={handleMarkAsRead}
                onClearAll={handleClearAll} />

            </div>
          </div>

          {/* Main Content Grid - All Content Below Banner */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <InstituteInfo institute={instituteData} videoUrls={videoUrls} resources={resources} applicationLink={applicationLink} />

              <TestimonialSection testimonials={testimonials} />

              <LivePresentationCard presentations={presentations} />
            </div>

            {/* Right Sidebar - Reordered: Apply Now (ResourceSidebar) now above Connect with Us (InteractionPanel) */}
            <div className="space-y-6 md:space-y-8">
              <ResourceSidebar
                resources={resources}
                applicationLink={applicationLink}
              />

              <InteractionPanel
                isAvailable={counsellorOnline}
                nextAvailableTime="Tomorrow at 9:00 AM"
                instituteId={id}
                instituteName={instituteData?.name}
                instituteEmail={instituteData?.contact?.email}
                institutePhone={instituteData?.contact?.phone}
                chatEnabled={instituteData?.chatEnabled ?? false}
                videoEnabled={instituteData?.videoEnabled ?? false}
                tour360Enabled={instituteData?.tour_360_enabled ?? false}
                whatsappEnabled={instituteData?.whatsappEnabled ?? false}
                counsellorOnline={counsellorOnline}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

};

export default VirtualBooth;